import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vet_admin_token";

// Backend supports BOTH httpOnly cookie auth AND Authorization: Bearer header.
// We use Bearer + localStorage on the frontend because it's bulletproof across
// every deployment topology (custom domains, cross-origin, CDN proxies). For a
// single trusted shop-owner admin login on the owner's own phone, the XSS-vs-
// CSRF tradeoff strongly favors this approach.
function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function setToken(t) {
  try { if (t) localStorage.setItem(TOKEN_KEY, t); else localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
}

function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Helper used by AdminDashboard for protected requests
export const authRequest = {
  get: (url) => axios.get(url, { headers: authHeaders() }),
  post: (url, body) => axios.post(url, body, { headers: authHeaders() }),
  patch: (url, body) => axios.patch(url, body, { headers: authHeaders() }),
  delete: (url) => axios.delete(url, { headers: authHeaders() }),
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = still checking session, null = signed out, object = signed in
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return null;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, { headers: authHeaders() });
      setUser(data);
      return data;
    } catch (e) {
      setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
